import { documentToReactComponents } from '@contentful/rich-text-react-renderer';
import { Document } from '@contentful/rich-text-types';
import { useRouter } from 'next/router';
import { Box } from '@zoopla-eng/zds/box';
import { ButtonLink } from '@zoopla-eng/zds/button';
import { CmsImage } from '@zoopla/cms/image';
import { logWarn } from '@zoopla/logger';
import { EventNames, trackSegmentAndGAEvent } from '@zoopla/tracking';
import ImagePlaceholder from '../assets/image-placeholder.svg';
import {
    bannerContentClass,
    imageClass,
    bannerTitleStyle,
    bannerContainerStyle
} from './banner.styles.css';
import { BANNER_RENDER_OPTIONS } from './render-options';
import type { EventTracking } from '../graphql.generated';
import { fontGroups } from '@zoopla-eng/zds/themes';

export type BannerButton = {
    label: string;
    url: string;
    category: 'primary' | 'secondary' | 'tertiary';
    eventTracking?: EventTracking;
};

export type BannerProps = {
    /**
     * defaults to `bottom`
     */
    imagePosition?: 'top' | 'bottom';
    imageSrc: string;
    imageAlt?: string;

    /**
     * defaults to `false`. `gridColumn` will overwrite.
     */
    fw?: boolean;

    title: string;
    body?: Document;

    /**
     * defaults to `brandPrimaryT4` which is `purple`
     */
    bgColor?: 'purple' | 'white' | 'green' | 'blue' | 'maroon' | 'orange';

    buttons: BannerButton[];
};

function trackingClick(trackingData: EventTracking, page: string) {
    const { eventCategory, eventAction, segmentEventName } = trackingData;

    const validSegmentEventName = Object.values(EventNames).find(
        (validEventName) => validEventName === segmentEventName
    );

    if (!validSegmentEventName) {
        const validSegmentEventNames = Object.values(EventNames).join(', ');
        logWarn(
            `Invalid segmentEventName: ${segmentEventName}.`,
            `Please use one of ${validSegmentEventNames}.`
        );
        return;
    }

    const GAEvent = {
        eventLabel: trackingData.eventLabel ?? undefined,
        eventCategory,
        eventAction
    };

    return trackSegmentAndGAEvent(validSegmentEventName, GAEvent, page);
}

export const Banner = ({
    imagePosition = 'bottom',
    fw = false,
    imageSrc,
    imageAlt = '',
    title,
    body,
    bgColor,
    buttons
}: BannerProps) => {
    const router = useRouter();

    const buttonsContent = buttons.map((button) => {
        const { category, label, url, eventTracking } = button;
        const onButtonClick = eventTracking
            ? () => {
                  trackingClick(eventTracking, `${router.pathname}/`);
              }
            : undefined;

        return (
            <ButtonLink
                key={label}
                category={category}
                {...{ as: 'a', href: url }}
                onClick={onButtonClick}
            >
                {label}
            </ButtonLink>
        );
    });

    return (
        <Box fw={fw}>
            <div className={bannerContainerStyle({ bgColor, imagePosition })}>
                <div className={bannerContentClass}>
                    <h2 className={`${bannerTitleStyle} ${fontGroups.plate}`}>
                        {title}
                    </h2>
                    <Box pt="S200" pb="S200">
                        {body
                            ? documentToReactComponents(
                                  body,
                                  BANNER_RENDER_OPTIONS
                              )
                            : null}
                    </Box>
                    <Box display="flex" flexWrap="wrap">
                        {buttonsContent}
                    </Box>
                </div>

                <CmsImage
                    imgClassName={imageClass}
                    pictureClassName={imageClass}
                    src={imageSrc}
                    alt={imageAlt}
                    banner={true}
                    onError={(e) => (e.currentTarget.src = ImagePlaceholder)}
                />
            </div>
        </Box>
    );
};
